"use client";

import type { Variants } from "framer-motion";
import { motion, useAnimation } from "framer-motion";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

import { cn } from "@/lib/utils";

export interface StarIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface StarIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  color?: string;
}

const STAR_VARIANTS: Variants = {
  initial: {
    scale: 1,
    fill: "none",
    rotate: 0,
  },
  hover: {
    scale: [1, 1.2, 1],
    fill: "currentColor",
    rotate: [0, 15, -15, 0],
    transition: {
      duration: 0.8,
      ease: "easeInOut",
    },
  },
};

const StarIcon = forwardRef<StarIconHandle, StarIconProps>(
  ({ onMouseEnter, onMouseLeave, className, size = 28, color = "currentColor", ...props }, ref) => {
    const starControls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => {
          starControls.start("hover");
        },
        stopAnimation: () => {
          starControls.start("initial");
        },
      };
    });

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseEnter?.(e);
        } else {
          starControls.start("hover");
        }
      },
      [onMouseEnter, starControls]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseLeave?.(e);
        } else {
          starControls.start("initial");
        }
      },
      [starControls, onMouseLeave]
    );

    return (
      <div
        className={cn(className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.path
            animate={starControls}
            d="m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
            variants={STAR_VARIANTS}
          />
        </svg>
      </div>
    );
  }
);

StarIcon.displayName = "StarIcon";

export { StarIcon };
